import * as React from 'react';
import { pipe, equals } from 'ramda';
import { Theme } from '@mui/material/styles';
import { withStyles } from 'tss-react/mui';
import { padToLength } from '../../../../../Utils/stringUtils';
import {
  getCurrentDate,
  restrictDateString,
} from '../../../../../Utils/dateUtils';
import {
  getNewAnalys,
  NEW_ANALYS_RAPPORT_ID,
} from './Components/SideMenu/Components/CreateNewAnalys/CreateNewAnalysUtils';
import { getAnalysFromUserId } from './Utils/userInfoUtils';
import { IUserInfo } from './Utils/ekonomiskAnalysInitQueryUtils';
import { rutinInfos } from '../../../_Common/Const/RutinInfo';
import { EK_KONTO_URVAL_COLLECTION_NAME } from '../../../../../Components/Urval/Consts/UrvalConsts';
import withErrorBoundary from '../../../../../hoc/withErrorBoundary';
import withActiveDatabaseName, {
  IWithActiveDatabaseName,
} from '../../../../../hoc/withActiveDatabaseName';
import EkonomiskAnalysSettingsQuery, {
  IColumn,
  ISumCategory,
} from './Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import EkonomiskAnalysInitQuery, {
  ISelection,
  IAnalys,
} from './Queries/EkonomiskAnalysInitQuery/EkonomiskAnalysInitQuery';
import { AnalysInitQuery_logo } from './Queries/EkonomiskAnalysInitQuery/_QueryTypes/AnalysInitQuery';
import EkonomiskAnalysBaseParamsQuery, {
  StandardColumnType,
} from './Queries/EkonomiskAnalysBaseParamsQuery/EkonomiskAnalysBaseParamsQuery';
import { withAddStartedRoutine } from '../../../../StartPanel/Components/Cards/StartedRoutines/startedRoutineQueries';
import { kodplaner_ekkodplaner } from '../../../../../Components/KodplanManager/Queries/_QueryTypes/kodplaner';
import AddToRecentAnalyserMutation, {
  AddToRecentAnalyserFnType,
} from './Components/SideMenu/EkonomiskAnalysSelect/AddToRecentAnalyserMutation/AddToRecentAnalyserMutation';
import ModelProvider, {
  ModelContextConsumer,
  ModelContext,
  AvenyCollectionMap,
} from '../../../../../Model/ModelContext/ModelContext';
import { MenuContext, IToggleOpen } from '../../../../MenuContext';
import { KodplanConsumer } from '../../../../../Components/KodplanManager/Context/KodplanContext';
import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { EkonomiskAnalysSelectProvider } from './Components/SideMenu/Components/AnalysSelect/Context/EkonomiskAnalysSelectContext';
import AnalysSettingsHandler, {
  IEkonomiskAnalysDates,
} from './Components/AnalysSettingsHandler/AnalysSettingsHandler';
import AvailableColumnTemplatesHandler, {
  getNewAnalysColumns,
} from './Components/SideMenu/Components/PeriodOchKolumner/Queries/AvailableColumnTemplatesHandler';
import EkonomiskAnalysMain from './Components/EkonomiskAnalysMain/EkonomiskAnalysMain';
import GraphLinkConverter from './Components/GraphLinkConverter/GraphLinkConverter';
import KodplanManager from '../../../../../Components/KodplanManager/KodplanManager';
import Kodbehorighetsurval from '../../../../../Components/Urval/Components/Kodbehorighetsurval/Kodbehorighetsurval';
import ReadonlyPlaceholderPage from '../../../_Common/Components/RutinAvailabilityContext/ReadonylyPlaceholderPage';
import EDAvenyProgress from '../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import {
  ActiveDatabaseContext,
  IAvailableRoutine,
} from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { ActiveRoutineContextProvider } from '../../../../Routing/ActiveRoutine/ActiveRoutineContext';
import DataErrorPage from '../../../../../Components/DataErrorPage/DataErrorPage';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import { AnalysTypeEnum } from './Const/EkonomiskAnalysConsts';
import { translateAnalysToAnalysTypeEnum } from './Utils/detaljanalysUtils';

export interface ISelectedAnalys {
  analys: ISelectedAnalysAnalys;
  codePrivilege: ISelectedAnalysCodePrivilege;
}

export interface ISelectedAnalysAnalys {
  id: string;
  name: string;
  utdelningLopnr: number | null;
  idkod?: string;
  grundurval?: IGrundurval;
  defaultFromYear?: number;
  defaultFromPeriod?: number;
  category?: string;
  categoryID?: number;
  ai_prompt?: string | null;
  detaljanalys?: boolean;
}

export interface ISelectedAnalysCodePrivilege {
  name: string;
  id: string;
  anvandsFor?: string | null;
  analysList?: IAnalys[];
}

export interface IGrundurval {
  name: string;
  id: string;
}

interface IProps extends IWithActiveDatabaseName {
  classes?: any;
}

interface IInnerProps extends IProps {
  activeKodplan: kodplaner_ekkodplaner;
  availableCategories: ISumCategory[];
  availableReports: ISelection[];
  userInfo: IUserInfo;
  standardColumns: StandardColumnType[] | undefined;
  avenyCollectionMap: AvenyCollectionMap;
  addToRecentAnalyser: AddToRecentAnalyserFnType;
  helaHuvudbokenGrundUrval?: IGrundurval;
  newAnalysColumns: IColumn[];
  hasRutinStartError: boolean;
  hasWholeCodeBook: boolean;
  ekLogo: AnalysInitQuery_logo;
}

interface IState {
  selectedAnalys: ISelectedAnalys | null;
  unsavedAnalys: boolean;
  ignoreStartAnalys: boolean;
  startDates: IEkonomiskAnalysDates;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  impersonatedCodePrivilegeChanged: boolean;
  leftMenuOpen: boolean;
}

const styles = (theme: Theme) => ({
  container: {
    maxWidth: '100vw',
    display: 'flex',
    flex: '1 1 auto',
    width: '100%',

    '@media print': {
      '& .MuiDrawer-root, .MuiTabs-root, .MuiButtonBase-root': {
        display: 'none',
      },
      '& .MuiBox-root ': {
        minWidth: '0 !important',
      },
      '& .MuiPaper-root ': {
        minWidth: '0 !important',
      },
    },
  },
});

class EkonomiskAnalysPage extends React.Component<IInnerProps, IState> {
  sideMenuSelectInputRef: React.RefObject<HTMLInputElement | null>;

  constructor(props: IInnerProps) {
    super(props);

    this.sideMenuSelectInputRef = React.createRef<HTMLInputElement>();

    const startDates = this.getStartDates();

    const selectedAnalys = getAnalysFromUserId(
      props.activeKodplan.id,
      props.availableReports,
      props.availableCategories,
      props.standardColumns,
      startDates,
      props.avenyCollectionMap,
      props.userInfo,
      props.helaHuvudbokenGrundUrval,
      props.newAnalysColumns,
      props.activeKodplan
    );

    this.state = {
      selectedAnalys: selectedAnalys,
      unsavedAnalys: false,
      ignoreStartAnalys: false,
      startDates: startDates,
      impersonatedCodePrivilegeChanged: false,
      leftMenuOpen:
        (props.userInfo.isSideMenuPinned ||
          !selectedAnalys ||
          selectedAnalys.analys.id === NEW_ANALYS_RAPPORT_ID ||
          !props.userInfo?.updateStartanalysAtLogin) &&
        !props.hasRutinStartError,
    };
  }

  componentDidUpdate(prevProps: IInnerProps) {
    if (
      prevProps.userInfo?.useCurrentDateAsStartDate !==
      this.props.userInfo?.useCurrentDateAsStartDate
    ) {
      const startDates = this.getStartDates();
      this.setState({
        startDates,
      });
    }

    if (
      !equals(prevProps.userInfo, this.props.userInfo) &&
      !this.state.selectedAnalys &&
      !this.state.ignoreStartAnalys
    ) {
      const selectedAnalys = getAnalysFromUserId(
        this.props.activeKodplan.id,
        this.props.availableReports,
        this.props.availableCategories,
        this.props.standardColumns,
        this.state.startDates,
        this.props.avenyCollectionMap,
        this.props.userInfo,
        this.props.helaHuvudbokenGrundUrval,
        this.props.newAnalysColumns,
        this.props.activeKodplan
      );

      const startDates = this.getStartDates();

      this.setState({
        selectedAnalys: selectedAnalys,
        unsavedAnalys: selectedAnalys?.analys.id === NEW_ANALYS_RAPPORT_ID,
        ignoreStartAnalys: false,
        startDates: startDates,
        impersonatedCodePrivilegeChanged: false,
      });
    }
  }

  getStartDates = (): IEkonomiskAnalysDates => {
    const bokfar = this.props.userInfo?.lastReportedPeriod?.year;
    const period = this.props.userInfo?.lastReportedPeriod?.month;

    const useOldMethod = this.props.userInfo?.useCurrentDateAsStartDate;
    let startDates: IEkonomiskAnalysDates;
    if (!useOldMethod && bokfar != null && period != null) {
      const from = {
        year: bokfar,
        month: period,
        day: 1,
      };
      startDates = {
        from: {
          ...from,
          userString: restrictDateString(
            `${from.year}-${padToLength(from.month, 2, '0')}-${padToLength(
              from.day,
              2,
              '0'
            )}`
          ),
        },
        to: {
          ...from,
          userString: '',
        },
      };
    } else {
      startDates = {
        from: getCurrentDate(),
        to: { ...getCurrentDate(), userString: '' },
      };
    }

    return startDates;
  };

  getAnalysId = (selectedAnalys: ISelectedAnalys | null) => {
    if (!selectedAnalys || selectedAnalys.analys.id === NEW_ANALYS_RAPPORT_ID) {
      return null;
    }

    return selectedAnalys.analys.id;
  };

  onSelectedAnalysUpdated = (
    analys: ISelectedAnalysAnalys,
    codePrivilege: ISelectedAnalysCodePrivilege,
    unsavedAnalys: boolean = false,
    callback?: () => void,
    newAnalys?: boolean
  ) => {
    let usedAnalys = analys;
    let usedCodePrivilege = codePrivilege;
    if (newAnalys) {
      usedAnalys = {
        ...getNewAnalys(
          this.props.availableCategories,
          this.props.standardColumns,
          this.state.startDates,
          this.props.avenyCollectionMap,
          this.props.newAnalysColumns,
          this.props.activeKodplan
        ),
        id: NEW_ANALYS_RAPPORT_ID,
        idkod: analys.idkod,
        utdelningLopnr: 1,
        name: 'Namnlös Analys',
        grundurval: this.props.helaHuvudbokenGrundUrval,
        category: analys.category,
        categoryID: analys.categoryID,
        detaljanalys: analys.detaljanalys,
        ai_prompt: analys.ai_prompt,
      };
      usedCodePrivilege = {
        id:
          this.props.availableReports.find(x => x.name === 'Hela huvudboken')
            ?.id ?? '',
        name: 'Hela huvudboken',
      };
    }

    this.setState(
      {
        selectedAnalys: {
          analys: usedAnalys,
          codePrivilege: usedCodePrivilege,
        },
        unsavedAnalys: unsavedAnalys,
        impersonatedCodePrivilege: undefined,
      },
      () => {
        if (
          this.props.userInfo &&
          this.props.userInfo.recentAnalyser &&
          !newAnalys
        ) {
          this.props.addToRecentAnalyser(
            usedAnalys,
            usedCodePrivilege,
            this.props.userInfo.recentAnalyser
          );
        }
        if (callback) {
          callback();
        }
      }
    );
  };

  deselectAnalys = (ignoreStartAnalys?: boolean) => {
    this.setState({
      selectedAnalys: null,
      ignoreStartAnalys: Boolean(ignoreStartAnalys),
    });
  };

  setStartDates = (newValues: IEkonomiskAnalysDates) => {
    this.setState({ startDates: newValues });
  };

  updateImpersonatedCodePrivilege = (
    codePrivilege?: ISelectedAnalysCodePrivilege
  ) => {
    this.setState({
      impersonatedCodePrivilege: codePrivilege,
      impersonatedCodePrivilegeChanged: true,
    });
  };

  setImpersonatedCodePrivilegeChanged = (value: boolean) => {
    this.setState({
      impersonatedCodePrivilegeChanged: value,
    });
  };

  onMenuToggle: IToggleOpen = (
    anchor: 'left' | 'right',
    value: boolean,
    callback?: () => void
  ) => {
    switch (anchor) {
      case 'left':
        this.setState(
          {
            leftMenuOpen: value,
          },
          callback
        );
        break;
    }
  };

  render() {
    const { selectedAnalys, unsavedAnalys, impersonatedCodePrivilege } =
      this.state;

    const grundurval =
      this.state.selectedAnalys && this.state.selectedAnalys.analys.grundurval
        ? this.state.selectedAnalys.analys.grundurval.id
        : '';

    return (
      <ModelProvider>
        <MenuContext.Provider
          value={{
            leftMenuOpen: this.state.leftMenuOpen,
            toggleOpen: this.onMenuToggle,
            rightMenuOpen: false,
          }}
        >
          <EkonomiskAnalysSelectProvider
            value={{
              selectInputRef: this.sideMenuSelectInputRef,
              selectAnalys: this.onSelectedAnalysUpdated,
              deselectAnalys: this.deselectAnalys,
              fullAnalysList: this.props.availableReports,
              selectedAnalys: this.state.selectedAnalys,
            }}
          >
            <ModelContextConsumer>
              {({ avenyCollectionMap }) => (
                <div className={this.props.classes.container}>
                  <GraphLinkConverter
                    availableReports={this.props.availableReports}
                    collectionMap={avenyCollectionMap}
                    standardColumns={this.props.standardColumns}
                    selectedAnalys={selectedAnalys}
                    onCloseLeftMenu={() => {
                      {
                        this.state.leftMenuOpen === true
                          ? this.setState({ leftMenuOpen: false })
                          : null;
                      }
                    }}
                    updateImpersonatedCodePrivilege={
                      this.updateImpersonatedCodePrivilege
                    }
                  >
                    {({
                      analysFromUrl,
                      settingsFromUrl,
                      sumsFromUrl,
                      analysLevel,
                      urvalConditionsIUrvalUsedForDrilling,
                      drilledFromDetaljanalys,
                    }) => {
                      const selectedAnalysToUse =
                        !selectedAnalys ||
                        (analysFromUrl &&
                          analysFromUrl.analys.id !== selectedAnalys.analys.id)
                          ? analysFromUrl
                          : selectedAnalys;

                      const analysType: AnalysTypeEnum =
                        translateAnalysToAnalysTypeEnum(
                          selectedAnalysToUse?.analys.idkod ?? ''
                        );

                      return (
                        <EkonomiskAnalysSettingsQuery
                          id={this.getAnalysId(selectedAnalysToUse)}
                          standardColumns={this.props.standardColumns}
                          standardColumnsQueryLoading={false}
                        >
                          {(analysSettings, loading) => {
                            const sysUrvalId = impersonatedCodePrivilege
                              ? impersonatedCodePrivilege.id
                              : selectedAnalysToUse
                              ? selectedAnalysToUse.codePrivilege.id
                              : undefined;
                            return (
                              <Kodbehorighetsurval
                                sysUrvalID={sysUrvalId}
                                collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
                              >
                                {({ kodbehorigheturval }) => (
                                  <Kodbehorighetsurval
                                    sysUrvalID={grundurval}
                                    collectionName={
                                      EK_KONTO_URVAL_COLLECTION_NAME
                                    }
                                  >
                                    {grundurvalValues => (
                                      <AnalysSettingsHandler
                                        analysLevel={analysLevel}
                                        analysType={analysType}
                                        drilledFromDetaljanalys={
                                          drilledFromDetaljanalys
                                        }
                                        loading={loading}
                                        selectedAnalys={selectedAnalysToUse}
                                        availableReports={
                                          this.props.availableReports
                                        }
                                        collectionMap={avenyCollectionMap}
                                        unsavedAnalys={unsavedAnalys}
                                        analysSettings={
                                          settingsFromUrl != null
                                            ? settingsFromUrl
                                            : selectedAnalysToUse?.analys.id ===
                                              NEW_ANALYS_RAPPORT_ID
                                            ? getNewAnalys(
                                                this.props.availableCategories,
                                                this.props.standardColumns,
                                                this.getStartDates(),
                                                avenyCollectionMap,
                                                this.props.newAnalysColumns,
                                                this.props.activeKodplan
                                              )
                                            : analysSettings
                                        }
                                        startDates={this.state.startDates}
                                        kodbehorighetsUrval={kodbehorigheturval}
                                        grundurvalUrval={
                                          grundurvalValues.kodbehorigheturval
                                        }
                                        kodplan={this.props.activeKodplan}
                                        sumsFromUrl={sumsFromUrl}
                                        standardColumns={
                                          this.props.standardColumns
                                        }
                                        setImpersonatedCodePrivilegeChanged={
                                          this
                                            .setImpersonatedCodePrivilegeChanged
                                        }
                                        originalStartDates={this.getStartDates()}
                                        urvalConditionsIUrvalUsedForDrilling={
                                          urvalConditionsIUrvalUsedForDrilling
                                        }
                                      >
                                        {settingsContext => (
                                          <EkonomiskAnalysMain
                                            analysLevel={analysLevel}
                                            analysType={analysType}
                                            drilledFromDetaljanalys={
                                              drilledFromDetaljanalys
                                            }
                                            settingsContext={settingsContext}
                                            impersonatedCodePrivilegeChanged={
                                              this.state
                                                .impersonatedCodePrivilegeChanged
                                            }
                                            userInfo={this.props.userInfo}
                                            codeSystemId={
                                              this.props.activeKodplan.id
                                            }
                                            activeKodplan={
                                              this.props.activeKodplan
                                            }
                                            activeDatabaseName={
                                              this.props.activeDatabaseName
                                            }
                                            selectedAnalys={
                                              analysFromUrl
                                                ? analysFromUrl
                                                : this.state.selectedAnalys
                                            }
                                            onSelectedAnalysUpdated={
                                              this.onSelectedAnalysUpdated
                                            }
                                            unsavedAnalys={unsavedAnalys}
                                            collectionMap={avenyCollectionMap}
                                            setStartDates={this.setStartDates}
                                            impersonatedCodePrivilege={
                                              this.state
                                                .impersonatedCodePrivilege
                                            }
                                            updateImpersonatedCodePrivilege={
                                              this
                                                .updateImpersonatedCodePrivilege
                                            }
                                            standardColumns={
                                              this.props.standardColumns
                                            }
                                            sideMenuOpen={
                                              this.state.leftMenuOpen
                                            }
                                            toggleSideMenuOpen={(
                                              value: boolean
                                            ) =>
                                              this.onMenuToggle('left', value)
                                            }
                                            originalStartDates={this.getStartDates()}
                                            hasRutinStartError={
                                              this.props.hasRutinStartError
                                            }
                                            hasWholeCodeBook={
                                              this.props.hasWholeCodeBook
                                            }
                                            logo={this.props.ekLogo}
                                            availableCategories={
                                              this.props.availableCategories
                                            }
                                          />
                                        )}
                                      </AnalysSettingsHandler>
                                    )}
                                  </Kodbehorighetsurval>
                                )}
                              </Kodbehorighetsurval>
                            );
                          }}
                        </EkonomiskAnalysSettingsQuery>
                      );
                    }}
                  </GraphLinkConverter>
                </div>
              )}
            </ModelContextConsumer>
          </EkonomiskAnalysSelectProvider>
        </MenuContext.Provider>
      </ModelProvider>
    );
  }
}

const EnhancedEkonomiskAnalysPage = (props: IProps) => {
  const { avenyCollectionMap } = React.useContext(ModelContext);
  const rutinAvailabilityInfo = React.useContext(RutinSettingsContext);
  const activeDatabaseContext = React.useContext(ActiveDatabaseContext);

  return (
    <EDBox
      key={activeDatabaseContext.activeDatabase}
      sx={{ display: 'flex', overflowY: 'hidden', flex: '1 1 auto' }}
    >
      <React.Fragment>
        <ActiveRoutineContextProvider
          rutinInfo={rutinInfos.rtnEkonomiskAnalys}
          collectionName={EK_KONTO_URVAL_COLLECTION_NAME}
        >
          <KodplanManager>
            <KodplanConsumer>
              {({ activeKodplan, loading: loadingKodplan, error }) => {
                if (loadingKodplan) {
                  return <EDAvenyProgress />;
                } else if (error || activeKodplan == null) {
                  const message = error
                    ? error.message
                    : 'Kunde inte hämta aktiv kodplan';
                  return message;
                } else {
                  return (
                    <EkonomiskAnalysInitQuery codeSystemId={activeKodplan.id}>
                      {({
                        availableAnalyser,
                        ekLogo,
                        hasListQueryData,
                        listQueryLoading,
                        userInfo,
                      }) => (
                        <AddToRecentAnalyserMutation recentAnalyser={[]}>
                          {addToRecentAnalyser => (
                            <EkonomiskAnalysBaseParamsQuery>
                              {({
                                availableCategories,
                                grundUrval,
                                hasWholeCodeBook,
                                loading,
                                standardColumns,
                              }) => {
                                const rutinInfo: IAvailableRoutine | undefined =
                                  activeDatabaseContext.availableRoutines?.[
                                    rutinInfos.rtnEkonomiskAnalys.rutinID
                                  ];

                                // Temporärt tills rutinen anses säker för läsläge
                                if (rutinInfo?.readonly) {
                                  return <ReadonlyPlaceholderPage />;
                                }
                                if (
                                  listQueryLoading ||
                                  loading ||
                                  !ekLogo ||
                                  !grundUrval ||
                                  !userInfo ||
                                  hasWholeCodeBook == null
                                ) {
                                  if (
                                    !listQueryLoading &&
                                    !loading &&
                                    !grundUrval
                                  ) {
                                    return (
                                      <DataErrorPage errorText="Det går inte att starta Ekonomisk Analys. Kontrollera att det finns utdelade rapporter med grundurval i rutin Rapportdefinitioner" />
                                    );
                                  }

                                  return <EDAvenyProgress />;
                                }
                                return (
                                  <AvailableColumnTemplatesHandler>
                                    {availableColumnTemplates => (
                                      <EkonomiskAnalysPage
                                        activeKodplan={activeKodplan}
                                        addToRecentAnalyser={
                                          addToRecentAnalyser
                                        }
                                        availableCategories={
                                          availableCategories ?? []
                                        }
                                        availableReports={
                                          availableAnalyser ?? []
                                        }
                                        avenyCollectionMap={avenyCollectionMap}
                                        ekLogo={ekLogo}
                                        userInfo={userInfo}
                                        standardColumns={standardColumns}
                                        helaHuvudbokenGrundUrval={grundUrval.find(
                                          x => x.name === 'Hela huvudboken'
                                        )}
                                        newAnalysColumns={getNewAnalysColumns(
                                          availableColumnTemplates
                                        )}
                                        hasRutinStartError={
                                          rutinAvailabilityInfo.hasRutinStartError
                                        }
                                        hasWholeCodeBook={hasWholeCodeBook}
                                        {...props}
                                      />
                                    )}
                                  </AvailableColumnTemplatesHandler>
                                );
                              }}
                            </EkonomiskAnalysBaseParamsQuery>
                          )}
                        </AddToRecentAnalyserMutation>
                      )}
                    </EkonomiskAnalysInitQuery>
                  );
                }
              }}
            </KodplanConsumer>
          </KodplanManager>
        </ActiveRoutineContextProvider>
      </React.Fragment>
    </EDBox>
  );
};

export default pipe(
  withAddStartedRoutine(rutinInfos.rtnEkonomiskAnalys),
  withErrorBoundary(rutinInfos.rtnEkonomiskAnalys.benamning)
)(withActiveDatabaseName(withStyles(EnhancedEkonomiskAnalysPage, styles)));
